import { useState, useCallback, useEffect, useRef } from "react";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSStatus } from "src/common/keys/protos/collections_pb";
import {
  Account, useAppDispatch, Message, MessageHandlerDisplayType, useSendRequestMutation, CollectionFilesyncAPI, DriveErrorMessages,
  useAppSelector, DriveRequest, getEnumKey, ErrorStackItem, useGetPermissions
} from "src/common";
import { PermissionSetType } from "src/common/drive/permissions.class";
import { DriveCallbackAsyncFunction, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";

/* Description: This is the get file size hook that returns the size of the given file. It is used to check
   the size of a video to see if it is too large to play in the browser. */
export function useGetFileSize(current_account: Account, use_filesync: boolean = false, include_deleted: boolean = false) {
  const dispatch = useAppDispatch();
  const default_permissions = useAppSelector((state: RootState) => state.drive.default_permissions);
  const [file_id, setFileId] = useState<string>();
  const [file_size_error, setError] = useState<boolean>(false);
  const [collection_id, setCollectionId] = useState<string>("");
  const [file_size, setFileSize] = useState<number>();
  const [sendRequest] = useSendRequestMutation();
  const { permissions, getPermissions, destroyPermissions } = useGetPermissions(current_account);

  const permissionsRef = useRef(permissions);
  function setPermissionsRef(_permissions?: PermissionSetType) {
    permissionsRef.current = _permissions;
  }

  // Description: Once permissions are set, get the file
  useEffect(() => {
    if (!!permissions && !!collection_id && !!file_id) {
      setPermissionsRef(permissions);
      destroyPermissions();
      getFile(collection_id, file_id);
    }
  }, [permissions]);

  // Description: Gets the file and sets size_size (for web and app)
  async function getFile(_collection_id: string, file_id: string) {
    setError(false);
    const drive_request = !!permissionsRef.current ? await CollectionFilesyncAPI.fetchFile(current_account, _collection_id, file_id, permissionsRef.current, include_deleted) : null;
    async function callback(message: FSMessage) {
      if (message.getStatus() === FSStatus.OK) {
        const file = message.getFile();
        if (!!file) {
          setFileSize(file.getFileSize());
        } else {
          handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_file }, message);
        }
      } else {
        handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_file }, message);
      }
    }
    handleSendRequest(drive_request, callback, DriveErrorMessages.error_fetching_file);
  }

  // Description: Handles error when the request object is null (this happens when permissions are undefined)
  function handleSendRequest<T>(request: DriveRequest | null, callback: DriveCallbackAsyncFunction<T>, stack_error: string = DriveErrorMessages.error_sending_request) {
    if (!!request) {
      request.setCallback(callback);
      sendRequest(request);
    } else {
      handlePageErrorMessage(DriveErrorMessages.default, { stack_error, stack_message: DriveErrorMessages.error_sending_request });
    }
  }

  // Description: Logs error message - set Error to true and send to store
  function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null,
    display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
    const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
    const stack = new ErrorStackItem("[useGetFileSize Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
    dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
    setError(true);
  }

  // Description: Callback for get file size - reset all states
  function resetFileSize() {
    setFileSize(undefined);
    setError(false);
    setCollectionId("");
  }

  // Description: Callback for get file size
  const getFileSize = useCallback((_collection_id: string, file_id: string) => {
    setFileId(file_id);
    setCollectionId(_collection_id);
    getPermissions(_collection_id, default_permissions);
  }, []);

  return {
    file_size,
    getFileSize,
    file_size_error,
    resetFileSize
  };
}
